/**
 * KnowledgeTag — Pure domain model (no React, no API imports).
 *
 * Tags use a flat-string hierarchy where "/" separates levels
 * (e.g. "customer/claims/prior-auth"). The full path is stored
 * as a single tag_name; there are no parent references.
 */
export class KnowledgeTag {
  constructor(
    readonly tagId: string,
    readonly clientId: string,
    readonly projectId: string | null,
    readonly tagName: string,
    readonly tagKey: string,
    readonly description: string | null,
    readonly usageCount: number,
    readonly createdAt: string,
  ) {}

  /** Hierarchy depth (1-based). "technology" → 1, "technology/data/warehouse" → 3. */
  getDepth(): number {
    return this.getSegments().length;
  }

  /** Individual hierarchy segments. "a/b/c" → ["a", "b", "c"]. */
  getSegments(): string[] {
    return this.tagName
      .split('/')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  /** Leaf segment displayed as a short label. "technology/data/warehouse" → "warehouse". */
  getLeafLabel(): string {
    const segments = this.getSegments();
    return segments.length > 0 ? segments[segments.length - 1] : this.tagName;
  }

  /** True when this tag's path starts with the given prefix path. */
  isDescendantOf(ancestorPath: string): boolean {
    const normalizedAncestor = ancestorPath.trim().toLowerCase();
    const normalizedSelf = this.tagName.trim().toLowerCase();
    if (normalizedSelf === normalizedAncestor) {
      return false;
    }
    return normalizedSelf.startsWith(normalizedAncestor + '/');
  }

  /** True when the tag name or any segment matches the search term (case-insensitive). */
  matchesSearch(query: string): boolean {
    const normalizedQuery = query.trim().toLowerCase();
    if (normalizedQuery.length === 0) {
      return true;
    }
    return this.tagName.toLowerCase().includes(normalizedQuery);
  }
}
